// Chemin: C:\gestion-copro\src\services\signalement.service.ts
// Service de gestion des signalements
// Gère l'upload des photos vers Storage et l'enregistrement dans Firestore
// Corrigé Session 24 : évite les champs undefined (Firestore les refuse)
// Session 126 : getSignalements + countSignalementsByStatut acceptent coproprieteId (fix permissions VIGIE)
// Session 212 : createSignalement écrit coproprieteId (étanchéité inter-copropriétés)

import { 
  collection, 
  addDoc, 
  doc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  query, 
  orderBy, 
  where,
  Timestamp
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL 
} from 'firebase/storage';
import { db, storage } from './firebase';
import { 
  Signalement, 
  SignalementFormData, 
  StatutSignalement
} from '../types/signalement.types';

// Type pour le résultat de création (exporté pour les composants)
export interface CreateSignalementResult {
  success: boolean;
  signalementId?: string;
  numeroSignalement?: string;
  error?: string;
}

// Nom de la collection Firestore
const COLLECTION_NAME = 'signalements';

/**
 * Génère un numéro de signalement unique
 * Format : SIG-YYYYMMDD-XXX (ex: SIG-20260121-001)
 */
const generateNumeroSignalement = async (): Promise<string> => {
  const today = new Date();
  const dateStr = today.toISOString().slice(0, 10).replace(/-/g, '');
  
  const startOfDay = new Date(today.setHours(0, 0, 0, 0));
  const endOfDay = new Date(today.setHours(23, 59, 59, 999));
  
  const q = query(
    collection(db, COLLECTION_NAME),
    where('dateSignalement', '>=', Timestamp.fromDate(startOfDay)),
    where('dateSignalement', '<=', Timestamp.fromDate(endOfDay))
  );
  
  const snapshot = await getDocs(q);
  const count = snapshot.size + 1;
  const countStr = count.toString().padStart(3, '0');
  
  return `SIG-${dateStr}-${countStr}`;
};

/**
 * Upload une photo vers Firebase Storage
 */
const uploadPhoto = async (file: File, signalementId: string): Promise<string> => {
  const timestamp = Date.now();
  const extension = file.name.split('.').pop();
  const fileName = `${timestamp}_${Math.random().toString(36).substring(7)}.${extension}`;
  const storageRef = ref(storage, `signalements/${signalementId}/${fileName}`);
  const snapshot = await uploadBytes(storageRef, file);
  return getDownloadURL(snapshot.ref);
};

/**
 * Upload plusieurs photos
 */
const uploadPhotos = async (files: File[], signalementId: string): Promise<string[]> => {
  return Promise.all(files.map(file => uploadPhoto(file, signalementId)));
};

/**
 * Crée un nouveau signalement
 * @param formData - Données du formulaire (inclut coproprieteId obligatoire)
 * @param declarantId - UID Firebase de l'utilisateur (pour VIGIE)
 */
export const createSignalement = async (
  formData: SignalementFormData,
  declarantId?: string
): Promise<CreateSignalementResult> => {
  try {
    const numeroSignalement = await generateNumeroSignalement();
    
    const declarant: Signalement['declarant'] = {
      nom: formData.nom,
    };
    if (formData.email?.trim()) declarant.email = formData.email.trim();
    if (formData.telephone?.trim()) declarant.telephone = formData.telephone.trim();
    if (formData.lot?.trim()) declarant.lot = formData.lot.trim();
    
    const signalementData: Record<string, unknown> = {
      coproprieteId: formData.coproprieteId,
      numeroSignalement,
      declarant,
      titre: formData.titre,
      description: formData.description,
      localisation: formData.localisation as Signalement['localisation'],
      localisationDetail: formData.localisationDetail,
      categorie: formData.categorie,
      urgence: formData.urgence as Signalement['urgence'],
      photos: [],
      statut: 'nouveau' as const,
      historique: [{
        date: Timestamp.now(),
        action: 'Signalement créé',
        details: `Signalement ${numeroSignalement} créé par ${formData.nom}`
      }],
      lieeGPA: false,
      dateSignalement: Timestamp.now(),
      dateModification: Timestamp.now(),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    };

    // Ajouter declarantId uniquement si fourni (VIGIE)
    if (declarantId) {
      signalementData.declarantId = declarantId;
    }

    const docRef = await addDoc(collection(db, COLLECTION_NAME), signalementData);
    const signalementId = docRef.id;

    if (formData.photos.length > 0) {
      const photoURLs = await uploadPhotos(formData.photos, signalementId);
      await updateDoc(doc(db, COLLECTION_NAME, signalementId), {
        photos: photoURLs,
        updatedAt: Timestamp.now()
      });
    }

    return { success: true, signalementId, numeroSignalement };

  } catch (error) {
    console.error('Erreur lors de la création du signalement:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Erreur inconnue'
    };
  }
};

/**
 * Récupère un signalement par son ID
 */
export const getSignalement = async (id: string): Promise<Signalement | null> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Signalement;
    }
    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du signalement:', error);
    return null;
  }
};

/**
 * Récupère tous les signalements
 * @param statut - Filtrer par statut (optionnel)
 * @param coproprieteId - Obligatoire — filtre par copropriété
 */
export const getSignalements = async (
  statut?: StatutSignalement,
  coproprieteId?: string
): Promise<Signalement[]> => {
  try {
    const constraints: Parameters<typeof query>[1][] = [];

    if (coproprieteId) {
      constraints.push(where('coproprieteId', '==', coproprieteId));
    }
    if (statut) {
      constraints.push(where('statut', '==', statut));
    }
    constraints.push(orderBy('dateSignalement', 'desc'));

    const q = query(collection(db, COLLECTION_NAME), ...constraints);
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })) as Signalement[];
  } catch (error) {
    console.error('Erreur lors de la récupération des signalements:', error);
    return [];
  }
};

/**
 * Met à jour le statut d'un signalement
 */
export const updateStatutSignalement = async (
  id: string, 
  statut: StatutSignalement,
  auteur: string = 'Système'
): Promise<boolean> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return false;
    
    const currentData = docSnap.data();
    const newHistorique = [
      ...currentData.historique,
      {
        date: Timestamp.now(),
        action: `Statut modifié`,
        details: `Statut passé de "${currentData.statut}" à "${statut}"`,
        auteur
      }
    ];
    
    await updateDoc(docRef, {
      statut,
      historique: newHistorique,
      dateModification: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    
    return true;
  } catch (error) {
    console.error('Erreur lors de la mise à jour du statut:', error);
    return false;
  }
};

/**
 * Compte les signalements par statut pour une copropriété
 */
export const countSignalementsByStatut = async (
  coproprieteId?: string
): Promise<Record<StatutSignalement, number>> => {
  const counts: Record<StatutSignalement, number> = {
    nouveau: 0,
    en_cours: 0,
    transmis_syndic: 0,
    transmis_promoteur: 0,
    resolu: 0,
    refuse: 0
  };
  
  try {
    const q = coproprieteId
      ? query(collection(db, COLLECTION_NAME), where('coproprieteId', '==', coproprieteId))
      : query(collection(db, COLLECTION_NAME));

    const snapshot = await getDocs(q);
    snapshot.docs.forEach(doc => {
      const statut = doc.data().statut as StatutSignalement;
      if (statut in counts) counts[statut]++;
    });
  } catch (error) {
    console.error('Erreur lors du comptage des signalements:', error);
  }
  
  return counts;
};
